import { X as CloseIcon, Loader2, Sparkles } from "lucide-react";
import React, { useState } from "react";
import { useToast } from "@/components/shared";
import { modelService } from "@/lib/services";
import type { ApiProtocol, ModelHosting, ModelType, ReasoningConfig } from "@/types";
import { EndpointProtocolFields } from "./EndpointProtocolFields";
import { ModelBehaviorFields } from "./ModelBehaviorFields";
import { toEndpointPayload } from "./modelEndpointConfig";

/** Map an AI-suggested provider string to the new hosting + protocol fields. */
function suggestToEndpoint(provider: string): { hosting: ModelHosting; api_protocol: ApiProtocol } {
  if (provider === "anthropic_foundry") {
    return { hosting: "foundry_eastus2", api_protocol: "anthropic_messages" };
  }
  if (provider === "azure_foundry" || provider === "azure_ai_foundry") {
    return { hosting: "foundry_eastus2", api_protocol: "openai_completions" };
  }
  return { hosting: "azure_openai_eastus2", api_protocol: "openai_responses" };
}

export interface AddModelModalProps {
  onClose: () => void;
  onSave: () => void;
}

export function AddModelModal({ onClose, onSave }: Readonly<AddModelModalProps>) {
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    hosting: "azure_openai_eastus2" as ModelHosting,
    api_protocol: "openai_responses" as ApiProtocol,
    endpoint_url: "",
    api_key_value: "",
    deployment_name: "",
    capabilities: [] as string[],
    context_window: 128000,
    max_output_tokens: 16384,
    cost_per_1k_input: 0.005,
    cost_per_1k_output: 0.015,
    is_enabled: true,
    display_order: 0,
    model_type: "chat" as ModelType,
    is_reasoning: false,
    supports_vision: false,
    supports_function_calling: true,
    supports_streaming: true,
    supports_code_interpreter: false,
    reasoning_config: null as ReasoningConfig | null,
    description: "",
    cost_tier: "",
  });
  const [saving, setSaving] = useState(false);

  const [suggestName, setSuggestName] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestNotes, setSuggestNotes] = useState("");

  const handleSuggest = async () => {
    if (!suggestName.trim()) {
      showToast("Enter a model name to get suggestions", "warning");
      return;
    }
    try {
      setSuggesting(true);
      setSuggestNotes("");
      const response = await modelService.suggest(suggestName.trim());
      const s = response.data;
      // Prefer explicit protocol/hosting from the suggestion; otherwise derive
      // from the legacy provider string.
      const derived = suggestToEndpoint(s.provider);
      setFormData({
        id: s.id,
        name: s.name,
        hosting: s.hosting ?? derived.hosting,
        api_protocol: s.apiProtocol ?? derived.api_protocol,
        endpoint_url: "",
        api_key_value: "",
        deployment_name: s.deploymentName,
        capabilities: s.capabilities || [],
        context_window: s.contextWindow,
        max_output_tokens: s.maxOutputTokens,
        cost_per_1k_input: s.costPer1kInput ?? 0,
        cost_per_1k_output: s.costPer1kOutput ?? 0,
        is_enabled: s.isEnabled,
        display_order: s.displayOrder,
        model_type: (s.modelType || "chat") as ModelType,
        is_reasoning: s.isReasoning,
        supports_vision: s.supportsVision,
        supports_function_calling: s.supportsFunctionCalling,
        supports_streaming: s.supportsStreaming,
        supports_code_interpreter: s.supportsCodeInterpreter,
        reasoning_config: s.reasoningConfig ?? null,
        description: "",
        cost_tier: "",
      });
      if (s.notes) {
        setSuggestNotes(s.notes);
      }
      showToast("Form pre-filled with AI suggestions. Review and adjust before saving.", "success");
    } catch (err) {
      console.error("Failed to get model suggestions:", err);
      showToast("Failed to get AI suggestions. Fill in the form manually.", "error");
    } finally {
      setSuggesting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.id || !formData.name || !formData.deployment_name) {
      showToast("Please fill in all required fields", "warning");
      return;
    }
    if (formData.hosting === "custom" && !formData.endpoint_url.trim()) {
      showToast("Custom endpoint requires an endpoint URL", "warning");
      return;
    }

    try {
      setSaving(true);
      const { hosting, api_protocol, endpoint_url, api_key_value, cost_tier, reasoning_config, ...rest } = formData;
      const payload: Record<string, unknown> = {
        ...rest,
        ...toEndpointPayload({ hosting, api_protocol, endpoint_url }),
        ...(cost_tier ? { cost_tier } : {}),
        reasoning_config: rest.is_reasoning ? reasoning_config : null,
      };
      if (hosting === "custom" && api_key_value) {
        payload.api_key_value = api_key_value;
      }
      await modelService.create(payload);
      showToast("Model created successfully", "success");
      onSave();
    } catch (err) {
      console.error("Failed to create model:", err);
      showToast("Failed to create model", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-surface-overlay flex items-center justify-center z-50 p-4">
      <div className="bg-surface-primary rounded-xl border border-border-primary w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-surface-primary border-b border-border-primary px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-content-primary">Add New Model</h2>
          <button onClick={onClose} className="p-2 hover:bg-interactive-hover rounded-lg transition-colors">
            <CloseIcon className="w-5 h-5 text-content-tertiary" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* AI Suggest Section */}
          <div className="p-4 bg-brand-primary/5 border border-brand-primary/20 rounded-lg">
            <label className="block text-sm font-medium text-content-secondary mb-1.5">
              <Sparkles className="w-4 h-4 inline-block mr-1.5 text-brand-primary" />
              Quick Setup — AI-assisted
            </label>
            <p className="text-xs text-content-quaternary mb-2">
              Type a model name (e.g. &quot;gpt-5.4&quot;, &quot;o3-deep-research&quot;, &quot;gpt-image-2&quot;) and
              let AI pre-fill the configuration.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={suggestName}
                onChange={(e) => setSuggestName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSuggest();
                  }
                }}
                placeholder="e.g. gpt-5.4, o3-deep-research, gpt-image-2"
                className="flex-1 px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
              />
              <button
                type="button"
                onClick={handleSuggest}
                disabled={suggesting}
                className="px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
              >
                {suggesting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Suggesting...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Suggest
                  </>
                )}
              </button>
            </div>
            {suggestNotes && (
              <p className="mt-2 text-xs text-amber-400 bg-amber-400/10 rounded-sm px-2 py-1">{suggestNotes}</p>
            )}
          </div>

          <div className="border-t border-border-primary" />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="add-model-id" className="block text-sm font-medium text-content-secondary mb-1.5">
                Model ID <span className="text-red-400">*</span>
              </label>
              <input
                id="add-model-id"
                type="text"
                value={formData.id}
                onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                placeholder="model-router"
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
                required
              />
              <p className="text-xs text-content-quaternary mt-1">Unique identifier</p>
            </div>

            <div>
              <label
                htmlFor="add-model-display-name"
                className="block text-sm font-medium text-content-secondary mb-1.5"
              >
                Display Name <span className="text-red-400">*</span>
              </label>
              <input
                id="add-model-display-name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="GPT-4o"
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
                required
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="add-model-deployment-name"
              className="block text-sm font-medium text-content-secondary mb-1.5"
            >
              Deployment Name <span className="text-red-400">*</span>
            </label>
            <input
              id="add-model-deployment-name"
              type="text"
              value={formData.deployment_name}
              onChange={(e) => setFormData({ ...formData, deployment_name: e.target.value })}
              placeholder="model-router"
              className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
              required
            />
            <p className="text-xs text-content-quaternary mt-1">
              The exact deployment name on the hosting resource — this is what we send upstream.
            </p>
          </div>

          {/* Endpoint + API Protocol (unified) */}
          <EndpointProtocolFields
            idPrefix="add-model"
            hosting={formData.hosting}
            apiProtocol={formData.api_protocol}
            endpointUrl={formData.endpoint_url}
            apiKeyValue={formData.api_key_value}
            onHostingChange={(hosting) => setFormData({ ...formData, hosting })}
            onApiProtocolChange={(api_protocol) => setFormData({ ...formData, api_protocol })}
            onEndpointUrlChange={(endpoint_url) => setFormData({ ...formData, endpoint_url })}
            onApiKeyValueChange={(api_key_value) => setFormData({ ...formData, api_key_value })}
          />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="add-model-context-window"
                className="block text-sm font-medium text-content-secondary mb-1.5"
              >
                Context Window
              </label>
              <input
                id="add-model-context-window"
                type="number"
                value={formData.context_window}
                onChange={(e) => setFormData({ ...formData, context_window: Number.parseInt(e.target.value, 10) })}
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
              />
            </div>

            <div>
              <label
                htmlFor="add-model-max-output-tokens"
                className="block text-sm font-medium text-content-secondary mb-1.5"
              >
                Max Output Tokens
              </label>
              <input
                id="add-model-max-output-tokens"
                type="number"
                value={formData.max_output_tokens}
                onChange={(e) => setFormData({ ...formData, max_output_tokens: Number.parseInt(e.target.value, 10) })}
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="add-model-cost-input" className="block text-sm font-medium text-content-secondary mb-1.5">
                Cost per 1K Input Tokens ($)
              </label>
              <input
                id="add-model-cost-input"
                type="number"
                step="0.000001"
                value={formData.cost_per_1k_input}
                onChange={(e) => setFormData({ ...formData, cost_per_1k_input: Number.parseFloat(e.target.value) })}
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
              />
            </div>

            <div>
              <label
                htmlFor="add-model-cost-output"
                className="block text-sm font-medium text-content-secondary mb-1.5"
              >
                Cost per 1K Output Tokens ($)
              </label>
              <input
                id="add-model-cost-output"
                type="number"
                step="0.000001"
                value={formData.cost_per_1k_output}
                onChange={(e) => setFormData({ ...formData, cost_per_1k_output: Number.parseFloat(e.target.value) })}
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
              />
            </div>
          </div>

          {/* Description + Cost Tier */}
          <div>
            <label htmlFor="add-model-description" className="block text-sm font-medium text-content-secondary mb-1.5">
              Description
            </label>
            <input
              id="add-model-description"
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Short description shown in the model selector"
              className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
            />
          </div>

          <div>
            <label htmlFor="add-model-cost-tier" className="block text-sm font-medium text-content-secondary mb-1.5">
              Cost Tier
            </label>
            <select
              id="add-model-cost-tier"
              value={formData.cost_tier}
              onChange={(e) => setFormData({ ...formData, cost_tier: e.target.value })}
              className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
            >
              <option value="">Not set</option>
              <option value="$">$ — Low cost</option>
              <option value="$$">$$ — Mid cost</option>
              <option value="$$$">$$$ — High cost</option>
            </select>
          </div>

          <ModelBehaviorFields
            idPrefix="add-model"
            modelType={formData.model_type}
            capabilities={{
              is_reasoning: formData.is_reasoning,
              supports_vision: formData.supports_vision,
              supports_function_calling: formData.supports_function_calling,
              supports_streaming: formData.supports_streaming,
              supports_code_interpreter: formData.supports_code_interpreter,
            }}
            onModelTypeChange={(model_type) => setFormData({ ...formData, model_type })}
            onCapabilityChange={(key, checked) => setFormData({ ...formData, [key]: checked })}
            reasoningConfig={formData.reasoning_config}
            onReasoningConfigChange={(reasoning_config) => setFormData({ ...formData, reasoning_config })}
          />

          <div className="flex items-center gap-3 p-4 bg-surface-secondary rounded-lg">
            <input
              type="checkbox"
              id="is_enabled_add"
              checked={formData.is_enabled}
              onChange={(e) => setFormData({ ...formData, is_enabled: e.target.checked })}
              className="w-4 h-4 rounded-sm border-border-secondary text-brand-primary focus:ring-brand-primary"
            />
            <label htmlFor="is_enabled_add" className="text-sm text-content-primary cursor-pointer">
              Enable this model for users
            </label>
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-border-primary">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                  Creating...
                </>
              ) : (
                "Create Model"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
